import { CartItem, GuestCartItem } from "../api-client/models";

const GUEST_CART_STORAGE_KEY = "guest_cart";

export interface GuestCartStoredItem extends CartItem {
  product_item_id: number;
}

interface GuestCartResponse {
  id: null;
  items: CartItem[];
  total_price: string;
}

const parseGuestCart = (): GuestCartStoredItem[] => {
  const rawCart = localStorage.getItem(GUEST_CART_STORAGE_KEY);

  if (!rawCart) {
    return [];
  }

  try {
    const parsedCart = JSON.parse(rawCart);
    return Array.isArray(parsedCart) ? parsedCart : [];
  } catch {
    return [];
  }
};

const persistGuestCart = (items: GuestCartStoredItem[]) => {
  localStorage.setItem(GUEST_CART_STORAGE_KEY, JSON.stringify(items));
};

const getCartTotal = (items: GuestCartStoredItem[]) =>
  items.reduce((accumulator, item) => accumulator + Number(item.price || 0) * Number(item.quantity || 0), 0);

export const getGuestCartItems = (): GuestCartStoredItem[] => parseGuestCart();

export const getGuestCartPayloadItems = (): GuestCartItem[] =>
  parseGuestCart().map((item) => ({
    product_item_id: item.product_item_id,
    quantity: Number(item.quantity || 0),
  }));

export const getGuestCartData = (): GuestCartResponse => {
  const items = parseGuestCart();

  return {
    id: null,
    items,
    total_price: getCartTotal(items).toFixed(2),
  };
};

export const addGuestCartItem = (item: GuestCartStoredItem) => {
  const currentItems = parseGuestCart();
  const existingIndex = currentItems.findIndex((currentItem) => currentItem.product_item_id === item.product_item_id);

  if (existingIndex >= 0) {
    const existingItem = currentItems[existingIndex];
    const nextQuantity = Number(existingItem.quantity || 0) + Number(item.quantity || 0);

    if (nextQuantity <= 0) {
      currentItems.splice(existingIndex, 1);
    } else {
      currentItems[existingIndex] = {
        ...existingItem,
        ...item,
        id: item.product_item_id,
        quantity: nextQuantity,
      };
    }
  } else if (Number(item.quantity || 0) > 0) {
    currentItems.push({
      ...item,
      id: item.product_item_id,
      quantity: Number(item.quantity || 0),
    });
  }

  persistGuestCart(currentItems);
  return getGuestCartData();
};

export const updateGuestCartItemQuantity = (productItemId: number, quantity: number) => {
  const currentItems = parseGuestCart();
  const nextItems = quantity <= 0
    ? currentItems.filter((item) => item.product_item_id !== productItemId)
    : currentItems.map((item) => item.product_item_id === productItemId
      ? { ...item, id: productItemId, quantity }
      : item);

  persistGuestCart(nextItems);
  return getGuestCartData();
};

export const removeGuestCartItem = (productItemId: number) => {
  const nextItems = parseGuestCart().filter((item) => item.product_item_id !== productItemId);
  persistGuestCart(nextItems);
  return getGuestCartData();
};

export const clearGuestCart = () => {
  localStorage.removeItem(GUEST_CART_STORAGE_KEY);
};